import { Dispatch } from "redux";
import { Category, SubCategory } from "../types/categoryTypes";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "../../firebase";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "../../utils";

export const FETCH_CATEGORIES_REQUEST = "FETCH_CATEGORIES_REQUEST";
export const FETCH_CATEGORIES_SUCCESS = "FETCH_CATEGORIES_SUCCESS";
export const FETCH_CATEGORIES_FAILURE = "FETCH_CATEGORIES_FAILURE";

// Shared by navigation and selling: one catalogue, project-scoped cache and reads.
let pendingCategories: Promise<{ categories: Category[] }> | null = null;
const cacheKey = `categoriesList:${db.app.options.projectId}:v2`;

export const fetchCategoriesRequest = () => ({
  type: FETCH_CATEGORIES_REQUEST,
});

export const fetchCategoriesSuccess = (categoryPageData: {
  categories: Category[];
}) => ({
  type: FETCH_CATEGORIES_SUCCESS,
  payload: categoryPageData,
});

export const fetchCategoriesFailure = (error: string) => ({
  type: FETCH_CATEGORIES_FAILURE,
  payload: error,
});

export const fetchCategories =
  (refresh = false) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchCategoriesRequest());

    try {
      if (pendingCategories) {
        dispatch(fetchCategoriesSuccess(await pendingCategories));
        return;
      }
      pendingCategories = (async () => {
        let categoriesData: { categories: Category[] } = {
          categories: [],
        };

        // Get categories data from local storage
        let data: { categories: Category[] } | null = null;
        try {
          if (!refresh) {
            const cached: unknown = getLocalStorageWithExpiry(cacheKey);
            if (
              cached &&
              typeof cached === "object" &&
              "categories" in cached &&
              Array.isArray(cached.categories) &&
              cached.categories.every(
                (item: Category) =>
                  item &&
                  typeof item.id === "string" &&
                  typeof item.name === "string" &&
                  (item.source === "catalogue" || item.source === "navigation"),
              )
            ) {
              data = cached as { categories: Category[] };
            }
          }
        } catch {
          /* Storage may be unavailable; the read-only catalogue still works. */
        }
        if (data) {
          categoriesData = data;
        } else {
          // Read the existing collections concurrently; neither operation writes.
          const categoriesQuery = query(collection(db, "product_categories"));
          const subcategoriesQuery = query(
            collection(db, "product_sub_categories"),
          );
          const [categoriesSnapshot, subcategoriesSnapshot] = await Promise.all(
            [getDocs(categoriesQuery), getDocs(subcategoriesQuery)],
          );

          const categoriesItems = categoriesSnapshot.docs.map(
            (doc) =>
              ({
                documentId: doc.id,
                ...doc.data(),
                id: doc.id,
                source: "catalogue",
              }) as Category,
          );
          const subcategoriesData = subcategoriesSnapshot.docs.map(
            (doc) =>
              ({
                documentId: doc.id,
                ...doc.data(),
                id: doc.id,
              }) as SubCategory,
          );

          // Associate subcategories with their respective categories
          const categoriesWithSubcategories = categoriesItems.map(
            (category) => ({
              ...category,
              subcategories: subcategoriesData.filter(
                (subcategory) =>
                  subcategory.product_category === category.serial,
              ),
            }),
          );

          const navigationCategories = [
            {
              id: "1",
              name: "Latest Toys",
              slug: "latest-toys",
              serial: "",
              documentId: "1",
            },
            {
              id: "2",
              name: "Popular Toys",
              slug: "popular-toys",
              serial: "",
              documentId: "2",
            },
            {
              id: "3",
              name: "Toys Under $10",
              slug: "toys-under-10",
              serial: "",
              documentId: "3",
            },
            {
              id: "4",
              name: "Toys Under $25",
              slug: "toys-under-25",
              serial: "",
              documentId: "4",
            },
            {
              id: "5",
              name: "Gender Neutral",
              slug: "gender-neutral",
              serial: "",
              documentId: "5",
            },
          ];
          const addedCategories: Category[] = [
            ...navigationCategories.map((category) => ({
              ...category,
              source: "navigation" as const,
            })),
            ...categoriesWithSubcategories,
          ];

          categoriesData.categories = addedCategories.sort((a, b) =>
            a.name === "Miscellaneous"
              ? 1
              : b.name === "Miscellaneous"
                ? -1
                : 0,
          );

          try {
            setLocalStorageWithExpiry(cacheKey, categoriesData, 30);
          } catch {
            /* Optional cache. */
          }
        }
        return categoriesData;
      })();
      const categoriesData = await pendingCategories;
      dispatch(fetchCategoriesSuccess(categoriesData));
    } catch (error) {
      dispatch(
        fetchCategoriesFailure(
          "We couldn't load the toy categories. Please try again.",
        ),
      );
    } finally {
      pendingCategories = null;
    }
  };
